import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import { ISnackbarOptions, useDebouncedSnackbar } from './use-snackbar.hooks';
import {
  getSentryExtrasFromValidationError,
  getValidationErrorMsg,
  isValidationError,
  logValidationError,
} from '@shared/validation/validation.helpers';
import { Extras } from '@sentry/core';
import {
  catchError,
  resolveCallbackFunction,
  wrapError,
} from '@shared/helpers/utils.helpers';
import {
  OptionalIf,
  OptionalValueErrorPromiseCallback,
  MaybeCallbackOrError,
} from '@shared/models/type-helpers.model';
import { AxiosError } from 'axios';
import { toUserFacingErrorMessage } from '@shared/constants/server-errors.constants';
import { guards } from '@shared/helpers/typeguards.helpers';

/**
 * Options for the error handler.
 * @param Type - The type of the value or function to handle
 * @param DefaultType - The default value for the error handler. This type will be returned on error.
 * @returns The options for the error handler.
 */
type ErrorHandlerOptions<DefaultType = undefined> = OptionalIf<
  {
    defaultValue: DefaultType;

    /**
     * The options for the snackbar.
     */
    snackbarOptions?: ISnackbarOptions;

    /**
     * Whether to disable the snackbar.
     */
    disableSnackbar?: boolean;

    /**
     * Metadata to add to the Sentry scope.
     */
    extras?: Extras;

    /**
     * The level to set for the Sentry scope.
     * Will be visible in Sentry to highlight severity of the error.
     */
    level?: Sentry.SeverityLevel;

    /**
     * When `false`, skips {@link logValidationError} (console). Sentry and snackbar behavior are unchanged.
     */
    logError?: boolean;
  },
  'defaultValue',
  DefaultType extends undefined ? true : false
>;

/**
 * A function that handles errors by logging them and sending them to Sentry.
 * @param fnOrItem - The function to wrap or a non-funtion item that could be an error
 * @param options - The options for the error handler. If DefaultType is defined we require options.defaultValue to be provided
 * @returns The result of the function or undefined if an error occured
 */
export type ErrorHandler<Type, DefaultType = undefined> = (
  fnOrItem: MaybeCallbackOrError<Type>,
  ...optionArgs: DefaultType extends undefined
    ? [ErrorHandlerOptions] | [] // If DefaultType is undefined we dont required options to be provided (hence empty array)
    : [ErrorHandlerOptions<DefaultType>] // If DefaultType is defined we must provide options.defaultValue
) => Type | DefaultType;

/**
 * Hook that handles errors by logging them and sending them to Sentry.
 * If the function returns an error, the error will be logged and sent to Sentry.
 * If the function returns an item, the item will be returned.
 * @param fnOrItem - The function to wrap or a non-funtion item that could be an error
 * @param options - The options for the error handler
 * @returns A function that handles errors by logging them and sending them to Sentry.
 */
export const useErrorHandler = <Type, DefaultType = undefined>(): ErrorHandler<
  Type,
  DefaultType
> => {
  const enqueueSnackbar = useDebouncedSnackbar();

  return useCallback<ErrorHandler<Type, DefaultType>>(
    (fnOrValue, options = {}) => {
      // Execute any callback functions and handle errors in the same place
      // An improvement would be to use the defaultValue parameter
      const { value, error } = catchError(
        fnOrValue,
        options.defaultValue as DefaultType,
      );

      if (error) {
        // Log validation errors in a nicer way (same hook consumers pass `logError: false` to silence)
        if (options.logError !== false) logValidationError(error);

        // Pass error to sentry
        Sentry.withScope((scope) => {
          if (options.level) scope.setLevel(options.level);
          if (options.extras)
            scope.setExtras({
              ...getSentryExtrasFromValidationError(error),
              ...options.extras,
            });
          Sentry.captureException(error);
        });

        // Show an error message to user if not disabled
        if (!options.disableSnackbar) {
          const snackbarOptions = options.snackbarOptions ?? {};
          enqueueSnackbar(
            snackbarOptions.errorMessage || getErrorResponseMessage(error),
            {
              variant: snackbarOptions.variant ?? 'error', // Default to error variant
              ...snackbarOptions,
            },
          );
        }
      }

      return value;
    },
    [enqueueSnackbar],
  );
};

/**
 * A function that handles errors by logging them and sending them to Sentry.
 * @param fnOrItem - The function to wrap or a non-funtion item that could be an error
 * @param options - The options for the error handler
 * @returns The result of the function or undefined if an error occured
 */
export type AsyncErrorHandler<Type, DefaultType = undefined> = (
  promiseOrCallback: OptionalValueErrorPromiseCallback<Type>,
  ...optionArgs: DefaultType extends undefined
    ? [ErrorHandlerOptions] | [] // If DefaultType is undefined we dont required options to be provided (hence empty array)
    : [ErrorHandlerOptions<DefaultType>] // If DefaultType is defined we must provide options.defaultValue
) => Promise<Type | DefaultType>;

/**
 * Hook that handles errors in async functions by logging them and sending them to Sentry.
 * @returns A function that handles errors by logging them and sending them to Sentry.
 * @param promiseOrCallback - The promise or callback to handle
 * @param options - The options for the error handler
 * @returns The result of the promise or undefined if an error occured
 */
export const useAsyncErrorHandler = <
  Type,
  DefaultType = undefined,
>(): AsyncErrorHandler<Type, DefaultType> => {
  const errorHandler = useErrorHandler<Type, DefaultType>();

  return useCallback<AsyncErrorHandler<Type, DefaultType>>(
    async (promiseOrCallback, ...optionArgs) => {
      try {
        // Use error handler if the callback return an error (not by throwing)
        return errorHandler(
          await resolveCallbackFunction(promiseOrCallback),
          ...optionArgs,
        );
      } catch (error) {
        return errorHandler(wrapError(error), ...optionArgs);
      }
    },
    [errorHandler],
  );
};

/** Reads `{ error: string }` from an {@link AxiosError} response body. */
const parseAxiosErrorBody = (error: AxiosError): string | undefined => {
  const responseData = error.response?.data;
  if (
    responseData &&
    typeof responseData === 'object' &&
    'error' in responseData &&
    typeof responseData.error === 'string'
  ) {
    return responseData.error;
  }

  const { request } = error;
  if (!request || typeof request.response !== 'string') return undefined;
  if (request.response.startsWith('<')) return undefined;

  // Guard against non-JSON bodies (e.g. plain-text "Internal Server Error")
  try {
    const parsed: unknown = JSON.parse(request.response);
    if (parsed && guards.is.object(parsed) && guards.is.string(parsed.error)) {
      return parsed.error;
    }
  } catch {
    return undefined;
  }
  return undefined;
};

/**
 * Maps an error to a user-facing string. Handles validation errors, Axios
 * errors (reading `response.data.error` or the raw request body), and plain
 * `Error` instances. Server error messages are translated via
 * {@link toUserFacingErrorMessage} before being returned.
 */
export const getErrorResponseMessage = (error: unknown): string => {
  const fallback = 'Something went wrong';

  if (isValidationError(error)) {
    return toUserFacingErrorMessage(getValidationErrorMsg(error));
  }

  if (error instanceof AxiosError) {
    if (error.status === 503) {
      return 'Server is under maintenance, please try again in a few minutes';
    }

    const serverErrorMessage = parseAxiosErrorBody(error);
    if (serverErrorMessage) {
      return toUserFacingErrorMessage(serverErrorMessage);
    }
    return fallback;
  }

  // Worker/Comlink and other paths: plain Error keeps message; subclass name is preserved
  if (error instanceof Error) {
    if (error.message.includes('Missing Refresh Token')) {
      location.reload();
      return 'Your session has expired.';
    }
    if (error.message) return toUserFacingErrorMessage(error.message);
  }

  return fallback;
};
